import React, { useState, useEffect } from 'react';
import {
  Shield, ShieldCheck, Buildings, TreeStructure, Hash, Ticket, Lightning, Info, ArrowClockwise,
  Check, X,
} from '@phosphor-icons/react';
import {
  getBranchNames as fetchBranchNames, getGroupCode as fetchGroupCode,
  getAuctionNumbers as fetchAuctionNumbers, updateBidLossPermission, saveData as saveAuditLog,
} from '../api/connectionRegistryApi';
import SearchableSelect from './SearchableSelect';
import { useLoginName, apiErrorMessage } from '../utils/auditUser';
import Toast from './Toast';
import SectionLock from './SectionLock';
import { NO_DATA_MESSAGE, NO_DATA_SUMMARY, isEmptyResult } from '../utils/noData';

function BidLossPermission() {
  const loginName = useLoginName();
  const [formKey, setFormKey] = useState(0);

  const [branchlist, setBranchlist] = useState([]);
  const [branchCodeList, setBranchCodeList] = useState([]); // groups
  const [auctions, setAuctions] = useState([]);

  const [cao, setCao] = useState('');
  const [group, setGroup] = useState('');
  const [auctionNumber, setAuctionNumber] = useState('');
  const [ticketNo, setTicketNo] = useState('');
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [reason, setReason] = useState('');

  const [branchschema, setBranchschema] = useState('');
  const [groupCode, setGroupCode] = useState(0);

  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingCao, setPendingCao] = useState('');
  const [pendingGroup, setPendingGroup] = useState('');
  const [pendingAuctionNumber, setPendingAuctionNumber] = useState('');
  const [pendingTicketNo, setPendingTicketNo] = useState('');
  const [pendingReason, setPendingReason] = useState('');

  useEffect(() => {
    fetchBranchNames().then((res) => setBranchlist(res || [])).catch(() => setBranchlist([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formKey]);

  const showToast = (severity, summary, detail, sticky = false) => {
    setToast({ severity, summary, detail, sticky });
    if (!sticky) setTimeout(() => setToast(null), 4000);
  };

  const resetFromBranch = () => {
    setBranchschema('');
    setGroupCode(0);
    setGroup('');
    setAuctionNumber('');
    setTicketNo('');
    setBranchCodeList([]);
    setAuctions([]);
  };

  const resetFromGroup = () => {
    setGroupCode(0);
    setAuctionNumber('');
    setTicketNo('');
    setAuctions([]);
  };

  const loadGroups = async (branch) => {
    const schema = branch.branch_code;
    setBranchschema(schema);
    try {
      const res = await fetchGroupCode(schema);
      setBranchCodeList(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setBranchCodeList([]);
    }
  };

  const loadAuctions = async (schema, gCode) => {
    try {
      const res = await fetchAuctionNumbers(schema, gCode);
      setAuctions(res || []);
      if (isEmptyResult(res)) showToast('warn', NO_DATA_SUMMARY, NO_DATA_MESSAGE);
    } catch {
      setAuctions([]);
      showToast('error', 'Load Failed', 'Failed to load auctions. Please try again.');
    }
  };

  const handleCaoChange = (value) => {
    setCao(value || '');
    resetFromBranch();
    if (!value) return;
    const selected = branchlist.find((x) => x.branch_name === value);
    if (selected) loadGroups(selected);
  };

  const handleGroupChange = (value) => {
    setGroup(value || '');
    resetFromGroup();
    if (!value) return;
    const selected = branchCodeList.find((x) => String(x.group_Code) === String(value));
    if (!selected) return;

    // GetGroupcodes returns chitgroup_id as a string (and '' when the column is
    // NULL). Number('') is 0, not NaN — so fall back to group_Code the way the
    // Angular screen does instead of silently querying chitgroupid=0, which
    // returns no auctions and leaves the form unusable.
    const raw = selected.chitgroup_id !== null && selected.chitgroup_id !== undefined && selected.chitgroup_id !== ''
      ? selected.chitgroup_id
      : selected.group_Code;
    const gCode = Number(raw);
    if (!Number.isFinite(gCode) || gCode <= 0) {
      showToast('warn', 'Invalid Group', 'The selected group has no valid chit group id.');
      return;
    }
    setGroupCode(gCode);
    loadAuctions(branchschema, gCode);
  };

  const handleAuctionChange = (value) => {
    setAuctionNumber(value || '');
    if (!value) {
      setTicketNo('');
      return;
    }
    const selected = auctions.find((x) => String(x.auction_number) === String(value));
    setTicketNo(selected?.ticketno ?? selected?.ticketNo ?? '');
  };

  const toggleConfirm = () => {
    setIsConfirmed((c) => !c);
    setTouched(true);
  };

  const caoInvalid = touched && !cao;
  const groupInvalid = touched && !group;
  const auctionInvalid = touched && !auctionNumber;
  const confirmInvalid = touched && !isConfirmed;
  const reasonInvalid = touched && (reason.trim().length < 10 || reason.length > 200);

  const isValid = !!cao && !!group && !!auctionNumber && isConfirmed
    && reason.trim().length >= 10 && reason.length <= 200;

  const handleSave = () => {
    setTouched(true);
    if (!isValid) {
      showToast('warn', 'Incomplete Form', 'Please fill all required fields correctly.');
      return;
    }

    // update-bidloss_permission takes chitgroupid as a non-nullable Int64 and
    // splices auction_number/ticketno straight into SQL. A blank or non-numeric
    // value here is rejected by model binding (or blows up the query) instead of
    // doing anything useful, so stop before the request rather than after.
    if (!branchschema || !Number.isFinite(groupCode) || groupCode <= 0
      || !Number.isFinite(Number(auctionNumber)) || !Number.isFinite(Number(ticketNo)) || ticketNo === '') {
      showToast('warn', 'Selection Incomplete', 'Auction / ticket details did not load correctly. Please re-select the group and auction number.');
      return;
    }

    setPendingCao(cao);
    setPendingGroup(group);
    setPendingAuctionNumber(auctionNumber);
    setPendingTicketNo(ticketNo);
    setPendingReason(reason.trim());

    setShowConfirmModal(true);
  };

  const cancelConfirm = () => {
    if (saving) return;
    setShowConfirmModal(false);
  };

  const confirmSave = async () => {
    setSaving(true);
    try {
      await updateBidLossPermission(branchschema, pendingAuctionNumber, pendingTicketNo, groupCode);

      const oldData = {
        cao: pendingCao, group: pendingGroup, auction_number: pendingAuctionNumber,
        ticketNo: pendingTicketNo, bidLossPermission: false,
      };
      const newData = {
        cao: pendingCao, group: pendingGroup, auction_number: pendingAuctionNumber,
        ticketNo: pendingTicketNo, bidLossPermission: true,
      };
      const audit = {
        schemaName: branchschema,
        loginName,
        loginTime: new Date().toISOString(),
        changeType: 'BID_LOSS_PERMISSION',
        oldData,
        newData,
        reason: pendingReason,
      };

      try {
        await saveAuditLog([audit]);
        setSaving(false);
        setShowConfirmModal(false);
        showToast('success', 'Success', 'Bid loss permission updated and audit saved successfully.', true);
        handleClear();
      } catch (auditErr) {
        setSaving(false);
        setShowConfirmModal(false);
        console.error('Audit save failed:', auditErr);
        showToast('warn', 'Partial Success', 'Permission updated, but audit log failed to save.', true);
      }
    } catch (err) {
      setSaving(false);
      setShowConfirmModal(false);
      console.error('Update failed:', err);
      showToast('error', 'Update Failed', apiErrorMessage(err, 'Could not update the bid loss permission. Please try again.'), true);
    }
  };

  const handleClear = () => {
    setFormKey((k) => k + 1);
    setCao('');
    resetFromBranch();
    setIsConfirmed(false);
    setReason('');
    setTouched(false);
  };

  return (
    <div className="nc-page" style={{ position: 'relative' }}>
      <Toast toast={toast} onClose={() => setToast(null)} testId="bidloss-toast" />

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', padding: '20px', position: 'relative' }} data-testid="bidloss-form">
        {saving && (
          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#002FA7', opacity: 0.6, borderRadius: '8px 8px 0 0' }} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '8px', background: '#EFF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Shield size={18} color="#002FA7" />
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: '600', color: '#002FA7', textTransform: 'uppercase' }}>Permissions</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#111827' }}>Bid Loss Permission</div>
          </div>
        </div>

        {/* SECTION A: Selection */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>A</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Selection</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Choose CAO branch, group and auction number</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              <Buildings size={13} /> CAO <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`cao-${formKey}`}
              options={branchlist}
              getLabel={(o) => o.branch_name}
              getValue={(o) => o.branch_name}
              value={cao}
              onChange={handleCaoChange}
              placeholder="Select CAO…"
              emptyMessage="No branches found"
              error={caoInvalid}
              testId="bidloss-cao-select"
            />
            {caoInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Required</div>}
          </div>

          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              <TreeStructure size={13} /> Group <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`group-${formKey}`}
              options={branchCodeList}
              getLabel={(o) => o.group_Code}
              getValue={(o) => o.group_Code}
              value={group}
              onChange={handleGroupChange}
              placeholder="Select group…"
              emptyMessage="Select a CAO first"
              disabled={!cao}
              error={groupInvalid}
              testId="bidloss-group-select"
            />
            {groupInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Required</div>}
          </div>

          <div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>
              <Hash size={13} /> Auction No <span style={{ color: '#DC2626' }}>*</span>
            </label>
            <SearchableSelect
              key={`auction-${formKey}`}
              options={auctions}
              getLabel={(o) => String(o.auction_number)}
              getValue={(o) => o.auction_number}
              value={auctionNumber}
              onChange={handleAuctionChange}
              placeholder="Select auction…"
              emptyMessage="Select a group first"
              disabled={!group}
              error={auctionInvalid}
              testId="bidloss-auction-select"
            />
            {auctionInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>Required</div>}
          </div>
        </div>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        {/* SECTION B: Ticket Details */}
        <SectionLock locked={!auctionNumber} hint="Select a CAO, group and auction number in section A first">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>B</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Ticket Details</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Auto-populated from the selected auction</div>
          </div>
        </div>

        <div style={{ maxWidth: '340px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px' }}>
              <Ticket size={13} /> Ticket No
            </label>
            <span style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '9px', fontWeight: '700', color: '#002FA7', background: '#EFF4FF', padding: '2px 6px', borderRadius: '4px' }}>
              <Lightning size={10} weight="fill" /> Auto
            </span>
          </div>
          <input
            className="input-field"
            style={{ fontSize: '13px', background: '#F9FAFB' }}
            value={ticketNo || ''}
            readOnly
            placeholder="Populated on auction select"
            data-testid="bidloss-ticket-no"
          />
        </div>
        </SectionLock>

        <div style={{ height: '1px', background: '#E5E7EB', margin: '20px 0' }} />

        {/* SECTION C: Confirmation */}
        <SectionLock locked={!auctionNumber} hint="Select a CAO, group and auction number in section A first">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#002FA7', color: 'white', fontSize: '12px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>C</div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Confirmation</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Confirm permission and provide a justification reason</div>
          </div>
        </div>

        <div
          onClick={toggleConfirm}
          style={{
            display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '14px',
            border: isConfirmed ? '2px solid #0F766E' : '1px solid #E5E7EB',
            background: isConfirmed ? '#F0FDFA' : 'white', borderRadius: '8px',
            cursor: 'pointer', transition: 'all 0.15s ease',
          }}
          data-testid="bidloss-confirm-card"
        >
          <div style={{
            width: '22px', height: '22px', minWidth: '22px', borderRadius: '6px', marginTop: '2px',
            border: isConfirmed ? '2px solid #0F766E' : '2px solid #94A3B8',
            background: isConfirmed ? '#0F766E' : 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.15s ease',
          }}>
            {isConfirmed && <Check size={14} color="white" weight="bold" />}
          </div>
          <div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>Allow Bid Loss Permission</div>
            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>
              I confirm that bid loss permission should be granted for the selected ticket and auction number.
            </div>
          </div>
        </div>
        {confirmInvalid && <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '4px' }}>You must confirm before saving</div>}

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px', marginBottom: '4px' }}>
          <label style={{ fontSize: '11px', fontWeight: '600', color: '#6B7280' }}>Reason <span style={{ color: '#DC2626' }}>*</span></label>
          <span style={{ fontSize: '11px', color: reason.length >= 10 ? '#059669' : reason.length > 180 ? '#D97706' : '#9CA3AF' }}>
            {reason.length} / 200
          </span>
        </div>
        <textarea
          className="input-field"
          style={{ fontSize: '13px', borderColor: reasonInvalid ? '#DC2626' : undefined }}
          rows={4}
          maxLength={200}
          placeholder="Enter a detailed reason for granting bid loss permission…"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          data-testid="bidloss-reason"
        />
        {reasonInvalid && (
          <div style={{ fontSize: '11px', color: '#DC2626', marginTop: '2px' }}>
            {reason.trim().length === 0 ? 'Required' : reason.trim().length < 10 ? 'Minimum 10 characters' : 'Maximum 200 characters'}
          </div>
        )}
        </SectionLock>

        {/* ACTION BAR */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#9CA3AF' }}>
            <Info size={13} /> Fields marked with <strong>*</strong> are required
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={handleClear} className="btn-secondary" disabled={saving} data-testid="bidloss-clear-button">
              <ArrowClockwise size={14} style={{ marginRight: '4px' }} /> Clear
            </button>
            <button type="button" onClick={handleSave} className="btn-primary" disabled={saving} data-testid="bidloss-save-button">
              <Check size={14} style={{ marginRight: '4px' }} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>

      {/* Confirm modal */}
      {showConfirmModal && (
        <div
          onClick={cancelConfirm}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 50,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: 'white', borderRadius: '10px', width: '420px', maxWidth: '90vw', overflow: 'hidden' }}
            data-testid="bidloss-confirm-modal"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #E5E7EB' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <ShieldCheck size={18} color="#002FA7" />
                <h5 style={{ margin: 0, fontSize: '15px', fontWeight: '700', color: '#111827' }}>Confirm Bid Loss Permission</h5>
              </div>
              <button type="button" onClick={cancelConfirm} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF' }}>
                <X size={16} />
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              <p style={{ fontSize: '13px', color: '#6B7280', marginTop: 0 }}>
                You&apos;re about to grant bid loss permission for the selected auction. Please review before confirming.
              </p>

              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Auction No</p>
                  <p style={{ fontSize: '13px', color: '#111827', margin: 0 }}>{pendingAuctionNumber}</p>
                </div>
                <Ticket size={14} color="#9CA3AF" />
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '11px', color: '#9CA3AF', margin: 0 }}>Ticket No</p>
                  <p style={{ fontSize: '13px', color: '#059669', fontWeight: '600', margin: 0 }}>{pendingTicketNo}</p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', fontSize: '12px', color: '#6B7280', background: '#F9FAFB', padding: '10px', borderRadius: '6px' }}>
                <Info size={14} style={{ flexShrink: 0, marginTop: '2px' }} />
                <span>This action will be recorded in the audit log with the reason: &quot;{pendingReason}&quot;</span>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '14px 20px', borderTop: '1px solid #E5E7EB' }}>
              <button type="button" onClick={cancelConfirm} disabled={saving} className="btn-secondary" data-testid="bidloss-confirm-cancel">
                Cancel
              </button>
              <button type="button" onClick={confirmSave} disabled={saving} className="btn-primary" data-testid="bidloss-confirm-save">
                <Check size={14} style={{ marginRight: '4px' }} /> Yes, Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default BidLossPermission;